import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, Receipt, Printer, Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHead, Card } from "@/pages/app/AppShell";
import { ConnectCard, PayoutRows } from "@/components/Payouts";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";

const blank = { kind: "invoice", title: "", amount: "", payer_name: "", payer_email: "", agreement_id: "", due_date: "", description: "" };
const PAYOUT_DONE = "transferred";

function ReceiptDialog({ inv, onClose }) {
  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white text-white max-w-md" data-testid="receipt-dialog">
        {inv && (
          <div className="print-area p-2">
            <DialogTitle className="font-display text-3xl font-normal">Receipt</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">{inv.receipt_number} · {fmtDateTime(inv.paid_at)}</p>
            <div className="mt-6 text-sm space-y-1">
              <p><b>From:</b> {inv.payer_name} ({inv.payer_email})</p>
              <p><b>To:</b> {inv.company_name}</p>
              <p><b>For:</b> {inv.number} · {inv.title} ({inv.kind})</p>
            </div>
            <div className="mt-6 border-t border-slate-200 pt-4 font-mono text-sm space-y-2">
              <div className="flex justify-between"><span>Amount</span><span>{money(inv.amount, 2)}</span></div>
              <div className="flex justify-between"><span>Transaction fee</span><span>{money(inv.fee, 2)}</span></div>
              <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2"><span>Total paid</span><span>{money(inv.total, 2)}</span></div>
            </div>
            <p className="mt-6 text-xs text-slate-500">Paid securely via Stripe · Balqis Book</p>
            <button onClick={() => window.print()} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFF7FB] text-[#2A1240] px-5 py-2.5 text-sm print:hidden" data-testid="receipt-print-button"><Printer className="h-4 w-4" />Print / save PDF</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Payments() {
  const [invoices, setInvoices] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [fee, setFee] = useState(2.5);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [receipt, setReceipt] = useState(null);

  const loadPayouts = () => api.get("/payouts").then((r) => setPayouts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const load = () => {
    api.get("/invoices").then((r) => setInvoices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    loadPayouts();
  };
  useEffect(() => {
    load();
    api.get("/agreements").then((r) => setAgreements(r.data || [])).catch(() => {});
    api.get("/billing").then((r) => setFee(r.data?.fee_percent ?? 2.5)).catch(() => {});
  }, []);

  const amt = Number(f.amount || 0);
  const feeAmt = Math.round(amt * fee) / 100;
  const create = async () => {
    try {
      const { data } = await api.post("/invoices", { ...f, amount: amt });
      toast.success(data?.emailed ? `Payment request emailed to ${data.payer_email}` : "Invoice created — share the payment link");
      setOpen(false);
      setF(blank);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const link = (i) => `${window.location.origin}/pay/${i.pay_token}`;
  const copy = (i) => { navigator.clipboard?.writeText(link(i)); toast.success("Payment link copied"); };
  const emailPayer = async (i) => {
    try { await api.post(`/invoices/${i.id}/email`); toast.success(`Payment link emailed to ${i.payer_email}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const confirmCash = async (i) => {
    try { await api.post(`/invoices/${i.id}/confirm-cash`); toast.success("Cash payment confirmed — receipt emailed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const paid = invoices.filter((i) => i.status === "paid");
  const sum = (xs, k) => xs.reduce((s, i) => s + (i[k] || 0), 0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="payments-page">
      <PageHead kicker={`Secure checkout · ${fee}% transaction fee`} title="Payments">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="new-invoice-button"><Plus className="h-4 w-4" />New request</button>
      </PageHead>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden bg-[#2A1240]/10 mb-6" data-testid="payments-summary">
        {[["Collected", money(sum(paid, "amount"), 2), "text-emerald-300"], ["Outstanding", money(sum(invoices.filter((i) => i.status === "open" || i.status === "cash_pending"), "amount"), 2), "text-[#2A1240]"],
          ["Fees paid by payers", money(sum(paid, "fee"), 2), "text-[#8E2BC4]"], ["Awaiting payout", money(sum(payouts.filter((p) => p.status !== PAYOUT_DONE), "net"), 2), "text-[#2A1240]"]].map(([l, v, c]) => (
          <div key={l} className="bg-[#FFFFFF] p-5"><p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">{l}</p><p className={`mt-2 font-mono text-xl ${c}`}>{v}</p></div>
        ))}
      </div>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") === "payouts" ? "payouts" : "invoices"}>
        <TabsList className="bg-[#FFFFFF] border border-[#2A1240]/10">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices & milestones</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-6">
          <Card className="divide-y divide-[#2A1240]/10">
            {!invoices.length && <p className="p-10 text-center text-slate-300" data-testid="invoices-empty">No payment requests yet. Create an invoice, deposit or milestone.</p>}
            {invoices.map((i) => (
              <div key={i.id} className="p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`invoice-row-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400"><span className="uppercase tracking-wider text-[#8E2BC4]">{i.kind}</span> · {i.number} · {i.payer_name}</p>
                  <p className="text-[#2A1240] truncate">{i.title}</p>
                  {i.due_date && <p className="text-xs text-slate-400">Due {fmtDate(i.due_date)}</p>}
                </div>
                <p className="font-mono text-[#2A1240]">{money(i.amount, 2)} <span className="text-xs text-slate-400">+ {money(i.fee, 2)} fee</span></p>
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${i.status === "paid" ? "bg-emerald-400 text-white" : i.status === "cash_pending" ? "bg-amber-300 text-white" : "bg-[#2A1240]/10 text-slate-200"}`} data-testid={`invoice-status-${i.id}`}>{i.status === "cash_pending" ? "cash reported" : i.status}{i.status === "paid" && i.payment_method ? ` · ${i.payment_method}` : ""}</span>
                <div className="flex gap-2">
                  {i.status === "cash_pending" && <button className="btn-gold !py-1.5 !px-3 !text-xs" onClick={() => confirmCash(i)} data-testid={`invoice-confirm-cash-${i.id}`}>Confirm cash received</button>}
                  {i.status === "paid" ? (
                    <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => setReceipt(i)} data-testid={`invoice-receipt-${i.id}`}><Receipt className="h-3.5 w-3.5" />Receipt</button>
                  ) : (
                    <>
                      <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => emailPayer(i)} data-testid={`invoice-email-${i.id}`}><Mail className="h-3.5 w-3.5" />Email</button>
                      <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => copy(i)} data-testid={`invoice-copy-link-${i.id}`}><Copy className="h-3.5 w-3.5" />Link</button>
                      <a className="btn-gold !py-1.5 !px-3 !text-xs" href={`/pay/${i.pay_token}`} target="_blank" rel="noreferrer" data-testid={`invoice-open-pay-${i.id}`}><ExternalLink className="h-3.5 w-3.5" />Pay page</a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="payouts" className="mt-6">
          <ConnectCard onChange={loadPayouts} />
          <PayoutRows payouts={payouts} reload={loadPayouts} />
        </TabsContent>
      </Tabs>
      <ReceiptDialog inv={receipt} onClose={() => setReceipt(null)} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-lg max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="invoice-dialog">
          <DialogTitle className="font-display text-2xl font-normal">Request a payment</DialogTitle>
          <div className="flex gap-2">
            {["invoice", "deposit", "milestone"].map((k) => (
              <button key={k} onClick={() => setF({ ...f, kind: k })} className={`flex-1 rounded-full py-2 text-sm capitalize ${f.kind === k ? "bg-[#2A1240] text-white font-bold" : "border border-[#2A1240]/15"}`} data-testid={`invoice-kind-${k}`}>{k}</button>
            ))}
          </div>
          <input className="field" placeholder="Title (e.g. Milestone 1 — campaign launch)" value={f.title} onChange={set("title")} data-testid="invoice-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <input className="field" placeholder="Payer name" value={f.payer_name} onChange={set("payer_name")} data-testid="invoice-payer-name-input" />
            <input className="field" type="email" placeholder="Payer email" value={f.payer_email} onChange={set("payer_email")} data-testid="invoice-payer-email-input" />
            <input className="field" type="number" min="1" placeholder="Amount ($)" value={f.amount} onChange={set("amount")} data-testid="invoice-amount-input" />
            <input className="field" type="date" value={f.due_date} onChange={set("due_date")} data-testid="invoice-due-input" />
          </div>
          <select className="field" value={f.agreement_id} onChange={set("agreement_id")} data-testid="invoice-agreement-select">
            <option value="">Link to agreement (optional)</option>
            {agreements.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
          <textarea className="field" rows={2} placeholder="Description" value={f.description} onChange={set("description")} data-testid="invoice-description-input" />
          <div className="rounded-xl bg-[#FBE9F3] p-4 font-mono text-sm space-y-1" data-testid="invoice-fee-preview">
            <div className="flex justify-between text-slate-300"><span>Amount</span><span>{money(amt, 2)}</span></div>
            <div className="flex justify-between text-slate-300"><span>Transaction fee ({fee}%)</span><span>{money(feeAmt, 2)}</span></div>
            <div className="flex justify-between text-[#2A1240] border-t border-[#2A1240]/10 pt-1"><span>Payer pays</span><span>{money(amt + feeAmt, 2)}</span></div>
          </div>
          <button className="btn-gold" disabled={!f.title || !f.payer_name || !f.payer_email || amt < 1} onClick={create} data-testid="invoice-save-button">Create payment request</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
